import { useRouter } from "hooks/useRouter";
import React, { memo, useMemo } from "react";

const TrainingReports = ({ loading, data }) => {
  const { push } = useRouter();
  
  // Determine number of weeks from the data (use first location's weekCounts length, or default to 30)
  const numWeeks = data?.length > 0 && data[0]?.weekCounts?.length 
    ? data[0].weekCounts.length 
    : 30;
  const weeks = Array.from({ length: numWeeks }, (_, i) => i + 1);
  
  // Calculate grand totals
  const grandTotal = useMemo(() => {
    if (!data || data.length === 0) {
      return { totalUsers: 0, weekTotals: weeks.map(() => 0) };
    }

    const totalUsers = data.reduce((sum, loc) => {
      // Calculate total from weekCounts
      const locationTotal = loc.weekCounts?.reduce((weekSum, week) => weekSum + (week.count || 0), 0) || 0;
      return sum + locationTotal;
    }, 0);

    const weekTotals = weeks.map((weekIndex) => {
      // weekIndex is 1-based, but array is 0-based
      const arrayIndex = weekIndex - 1;
      return data.reduce((weekSum, loc) => {
        const weekData = loc.weekCounts?.[arrayIndex];
        return weekSum + (weekData?.count || 0);
      }, 0);
    });

    return { totalUsers, weekTotals };
  }, [data, weeks]);

  return (
    <div className="card overflow-x-auto">
      {loading ? (
        <p className="text-center text-sm text-ink-muted p-6 animate-pulse-soft">Loading, please wait…</p>
      ) : (
        <table className="min-w-full relative table-auto border-collapse overflow-auto text-[13px]">
          <thead>
            <tr className="bg-surface border-b border-line">
              <th className="sticky left-0 bg-surface text-left px-4 py-2.5 border-r border-line">
                Location
              </th>
              {weeks.map((week) => (
                <th
                  key={week}
                  className="whitespace-nowrap px-4 py-2.5 text-center"
                >
                  Week {week}
                </th>
              ))}
              <th className="px-4 py-2.5 sticky right-0 bg-white text-center font-semibold text-ink tabular-nums border-l border-line">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="relative divide-y divide-line">
            {data?.map((locationData) => {
              // Calculate total for this location
              const locationTotal = locationData.weekCounts?.reduce(
                (sum, week) => sum + (week.count || 0),
                0
              ) || 0;

              return (
                <tr key={locationData._id || locationData.office_name}>
                  <td className="sticky left-0 bg-white whitespace-nowrap px-4 py-2.5 font-medium text-ink border-r border-line">
                    {locationData.office_name || locationData.location}
                  </td>
                  {weeks.map((week) => {
                    // week is 1-based, array is 0-based
                    const weekIndex = week - 1;
                    const weekData = locationData.weekCounts?.[weekIndex];
                    const count = weekData?.count || 0;
                    const trainings = weekData?.trainings || [];
                    const trainingIds = trainings.map((t) => t._id).filter(Boolean);

                    return (
                      <td
                        key={week}
                        onClick={() => {
                          if (trainingIds.length > 0) {
                            push(`/training-users?id=${trainingIds.join(',')}`);
                          }
                        }}
                        className={`text-center px-4 py-2.5 tabular-nums ${
                          trainingIds.length > 0 
                            ? 'cursor-pointer hover:bg-gray-50' 
                            : ''
                        }`}
                      >
                        {count > 0 ? <p>{count}</p> : "-"}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2.5 sticky right-0 bg-white text-center font-semibold text-ink tabular-nums border-l border-line">
                    {locationTotal}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-surface border-t border-line">
              <td className="sticky left-0 bg-surface px-4 py-2.5 border-r border-line whitespace-nowrap">
                Grand Total
              </td>
              {grandTotal.weekTotals.map((weekTotal, index) => (
                <td key={index} className="px-4 py-2.5 text-center">
                  {weekTotal}
                </td>
              ))}
              <td className="px-4 py-2.5 sticky right-0 bg-surface text-center border-l border-line">
                {grandTotal.totalUsers}
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
};

export default memo(TrainingReports)